import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Ban, Copy, Download, Pause, Play, Square, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { CampaignProgress } from '../components/push-campaigns/CampaignProgress';
import { RecipientsTable } from '../components/push-campaigns/RecipientsTable';
import { PushCampaignStatusBadge } from '../components/push-campaigns/PushCampaignStatusBadge';
import { usePolling } from '../hooks/usePolling';
import { useAuth } from '../context/AuthContext';
import { canEditPushCampaigns, canLaunchPushCampaigns } from '../utils/pushCampaignPermissions';
import {
  formatPushDateTime,
  getPushCampaignFailureMessage,
  PUSH_TIMEZONE,
  toPushCampaignDuplicatePayload,
} from '../utils/pushCampaignValidation';
import {
  cancelPushCampaign,
  createPushCampaign,
  deletePushCampaign,
  downloadPushCampaignFailedCsv,
  getPushCampaign,
  getPushCampaignStats,
  isPushCampaignConflict,
  pausePushCampaign,
  resumePushCampaign,
  stopPushCampaign,
  type PushAudienceType,
  type PushCampaign,
  type PushCampaignStats,
  type PushCampaignStatus,
  type PushPausedReason,
} from '../services/resortAdminApi';

const DETAILS_POLL_INTERVAL_MS = 3000;

const POLLED_STATUSES: PushCampaignStatus[] = ['Sending', 'Stopping', 'Paused'];

const AUDIENCE_LABELS: Record<PushAudienceType, string> = {
  test_list: 'Test list',
  app_users: 'App users',
  partners: 'Partners',
  couriers: 'Couriers',
};

const PAUSED_REASON_LABELS: Record<PushPausedReason, string> = {
  error_spike: 'too many transport errors',
  content_rejected: 'the content snapshot changed',
  quiet_hours: `the quiet hours in ${PUSH_TIMEZONE}`,
  manual: 'an administrator paused it',
};

const FAILURE_REASON_LABELS: Record<string, string> = {
  auth_failure: 'Firebase rejected the credentials',
  launch_not_authorized: 'the launch attestation did not pass',
  stopped_by_admin: 'stopped by an administrator',
  materialization_error: 'the recipient registry could not be built',
  empty_audience_at_launch: 'the audience was empty at launch',
};

type PendingAction = 'pause' | 'stop' | 'resume' | 'cancel' | 'delete';

function formatDateTime(value: string | null): string {
  if (value == null || value === '') {
    return '-';
  }
  const parsed = new Date(value.replace(' ', 'T'));
  if (isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const PushCampaignDetails: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const campaignName = params.name ?? '';
  const canControl = canLaunchPushCampaigns(auth.user?.role ?? null);
  const canDuplicate = canEditPushCampaigns(auth.user?.role ?? null);

  const [campaign, setCampaign] = useState<PushCampaign | null>(null);
  const [stats, setStats] = useState<PushCampaignStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [isActing, setIsActing] = useState(false);
  const [isConflictOpen, setIsConflictOpen] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [includeFailedEmails, setIncludeFailedEmails] = useState(false);

  const reload = useCallback(async () => {
    if (campaignName === '') {
      return;
    }
    try {
      const loaded = await Promise.all([getPushCampaign(campaignName), getPushCampaignStats(campaignName)]);
      setCampaign(loaded[0]);
      setStats(loaded[1]);
      setError(null);
    } catch (failure) {
      setError(getPushCampaignFailureMessage(failure, (failure as Error)?.message || 'Failed to load the campaign.'));
    }
  }, [campaignName]);

  useEffect(() => { reload(); }, [reload]);

  const statsFetcher = useCallback(
    (signal: AbortSignal) => getPushCampaignStats(campaignName, signal),
    [campaignName]
  );

  const isPolled = stats != null && POLLED_STATUSES.includes(stats.status);
  const polling = usePolling(statsFetcher, isPolled, DETAILS_POLL_INTERVAL_MS);
  const polledStats = polling.data;

  useEffect(() => {
    if (polledStats == null) {
      return;
    }
    setStats(polledStats);
  }, [polledStats]);

  const runAction = async (action: PendingAction) => {
    if (campaign == null || isActing) {
      return;
    }
    setIsActing(true);
    setActionError(null);
    try {
      if (action === 'pause') {
        setCampaign(await pausePushCampaign(campaign.name, campaign.modified));
      } else if (action === 'stop') {
        setCampaign(await stopPushCampaign(campaign.name, campaign.modified));
      } else if (action === 'resume') {
        setCampaign(await resumePushCampaign(campaign.name, campaign.modified));
      } else if (action === 'cancel') {
        setCampaign(await cancelPushCampaign(campaign.name, campaign.modified));
      } else {
        await deletePushCampaign(campaign.name, campaign.modified);
        navigate('/push-campaigns');
        return;
      }
      setPendingAction(null);
      await reload();
    } catch (failure) {
      setPendingAction(null);
      if (isPushCampaignConflict(failure)) {
        setIsConflictOpen(true);
      } else {
        setActionError(getPushCampaignFailureMessage(failure, (failure as Error)?.message || 'The action failed.'));
      }
    } finally {
      setIsActing(false);
    }
  };

  const duplicate = async () => {
    if (campaign == null || isDuplicating) {
      return;
    }
    setIsDuplicating(true);
    setActionError(null);
    try {
      const created = await createPushCampaign(toPushCampaignDuplicatePayload(campaign));
      navigate(`/push-campaigns/${encodeURIComponent(created.name)}/edit?step=1`);
    } catch (failure) {
      setActionError(getPushCampaignFailureMessage(failure, (failure as Error)?.message || 'The campaign could not be duplicated.'));
    } finally {
      setIsDuplicating(false);
    }
  };

  const downloadFailed = async () => {
    if (campaign == null || isDownloading) {
      return;
    }
    setIsDownloading(true);
    setActionError(null);
    try {
      const blob = await downloadPushCampaignFailedCsv(campaign.name, includeFailedEmails);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `push-campaign-${campaign.name}-failed.csv`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (failure) {
      setActionError(getPushCampaignFailureMessage(failure, (failure as Error)?.message || 'The report could not be downloaded.'));
    } finally {
      setIsDownloading(false);
    }
  };

  if (error != null && campaign == null) {
    return (
      <div className="space-y-4">
        <div className="border-l-4 border-red-500 bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
        <button onClick={() => navigate('/push-campaigns')} className="text-sm text-alps-primary">
          Back to campaigns
        </button>
      </div>
    );
  }

  if (campaign == null || stats == null) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }

  const canPause = canControl && stats.status === 'Sending';
  const canStop = canControl && (stats.status === 'Sending' || stats.status === 'Paused');
  const canResume = canControl && stats.status === 'Paused';
  const canCancel = canControl && stats.status === 'Scheduled';
  const canDelete = canControl && stats.status === 'Draft';
  const canDownloadFailed = canControl && stats.failed_count > 0;
  const resumeAfter = formatPushDateTime(stats.resume_after ?? campaign.resume_after ?? null);

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate('/push-campaigns')}
          className="mb-2 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to campaigns
        </button>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold text-gray-800">{campaign.campaign_name}</h1>
            <PushCampaignStatusBadge status={stats.status} />
            {campaign.audience_type === 'test_list' && (
              <span className="inline-flex rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-800">
                Test
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canDuplicate && (
              <button
                onClick={duplicate}
                disabled={isDuplicating}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <Copy className="mr-2 h-4 w-4" />
                {isDuplicating ? 'Duplicating...' : 'Duplicate'}
              </button>
            )}
            {canPause && (
              <button
                onClick={() => setPendingAction('pause')}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Pause className="mr-2 h-4 w-4" />
                Pause
              </button>
            )}
            {canStop && (
              <button
                onClick={() => setPendingAction('stop')}
                className="inline-flex items-center rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                <Square className="mr-2 h-4 w-4" />
                Stop
              </button>
            )}
            {canResume && (
              <button
                onClick={() => runAction('resume')}
                disabled={isActing}
                className="inline-flex items-center rounded-md border border-transparent bg-alps-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-alps-primary-dark disabled:opacity-50"
              >
                <Play className="mr-2 h-4 w-4" />
                Resume
              </button>
            )}
            {canCancel && (
              <button
                onClick={() => setPendingAction('cancel')}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Ban className="mr-2 h-4 w-4" />
                Cancel
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => setPendingAction('delete')}
                className="inline-flex items-center rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {actionError != null && (
        <div className="border-l-4 border-red-500 bg-red-50 p-4">
          <p className="text-sm text-red-700">{actionError}</p>
        </div>
      )}

      {stats.status === 'Paused' && !!stats.paused_reason && (
        <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4 text-sm text-yellow-800">
          Sending is paused: {PAUSED_REASON_LABELS[stats.paused_reason] ?? stats.paused_reason}.
          {stats.paused_reason === 'quiet_hours' && resumeAfter != null && (
            <span> Sending can continue from {resumeAfter} {PUSH_TIMEZONE}.</span>
          )}
        </div>
      )}

      {!!stats.failure_reason && (
        <div className="border-l-4 border-red-500 bg-red-50 p-4 text-sm text-red-700">
          {FAILURE_REASON_LABELS[stats.failure_reason] ?? stats.failure_reason}.
        </div>
      )}

      {polling.isPaused && (
        <div className="flex items-center justify-between border-l-4 border-orange-500 bg-orange-50 p-4">
          <p className="text-sm text-orange-700">
            Live updates stopped after repeated errors. {polling.error ?? ''}
          </p>
          <button
            onClick={polling.retry}
            className="rounded-md border border-orange-300 bg-white px-3 py-1 text-sm font-medium text-orange-700 hover:bg-orange-100"
          >
            Retry
          </button>
        </div>
      )}

      <div className="rounded-lg bg-white p-6 shadow">
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 text-sm">
          <div>
            <dt className="text-gray-500">Audience</dt>
            <dd className="font-medium text-gray-900">{AUDIENCE_LABELS[campaign.audience_type] || campaign.audience_type}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Scheduled</dt>
            <dd className="font-medium text-gray-900">{formatDateTime(campaign.scheduled_at)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Started</dt>
            <dd className="font-medium text-gray-900">{formatDateTime(stats.started_at)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Finished</dt>
            <dd className="font-medium text-gray-900">{formatDateTime(stats.finished_at)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Last progress</dt>
            <dd className="font-medium text-gray-900">{formatDateTime(stats.last_progress_at)}</dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-gray-400">All times are shown in {PUSH_TIMEZONE}.</p>
      </div>

      <div className="rounded-lg bg-white p-6 shadow">
        <CampaignProgress stats={stats} />
        {canDownloadFailed && (
          <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-gray-100 pt-4">
            <button
              onClick={downloadFailed}
              disabled={isDownloading}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="mr-2 h-4 w-4" />
              {isDownloading ? 'Preparing...' : 'Download failed recipients'}
            </button>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeFailedEmails}
                onChange={x => setIncludeFailedEmails(x.target.checked)}
              />
              <span>Include email addresses</span>
            </label>
          </div>
        )}
      </div>

      {stats.status !== 'Draft' && (
        <RecipientsTable campaignName={campaign.name} refreshToken={stats.status} />
      )}

      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-sm font-semibold text-gray-900">Content</h3>
        <div className="mt-3 grid gap-4 lg:grid-cols-2 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">English</div>
            <div className="font-medium text-gray-900">{campaign.title_en || '-'}</div>
            <div className="text-gray-700">{campaign.body_en || '-'}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">French</div>
            <div className="font-medium text-gray-900">{campaign.title_fr || '-'}</div>
            <div className="text-gray-700">{campaign.body_fr || '-'}</div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={pendingAction === 'pause'}
        title="Pause the campaign"
        message="Sending stops after the current batch. Recipients that were not processed yet stay in the queue and are sent when the campaign is resumed."
        confirmLabel="Pause sending"
        variant="warning"
        loading={isActing}
        onConfirm={() => runAction('pause')}
        onCancel={() => setPendingAction(null)}
      />

      <ConfirmDialog
        open={pendingAction === 'stop'}
        title="Stop the campaign"
        message="Sending stops as soon as the current batch finishes. Recipients that were not processed yet are marked as cancelled. This cannot be undone."
        confirmLabel="Stop sending"
        variant="danger"
        loading={isActing}
        onConfirm={() => runAction('stop')}
        onCancel={() => setPendingAction(null)}
      />

      <ConfirmDialog
        open={pendingAction === 'cancel'}
        title="Cancel the campaign"
        message="The scheduled campaign will not be sent."
        confirmLabel="Cancel campaign"
        variant="warning"
        loading={isActing}
        onConfirm={() => runAction('cancel')}
        onCancel={() => setPendingAction(null)}
      />

      <ConfirmDialog
        open={pendingAction === 'delete'}
        title="Delete the campaign"
        message="The draft will be removed permanently."
        confirmLabel="Delete"
        variant="danger"
        loading={isActing}
        onConfirm={() => runAction('delete')}
        onCancel={() => setPendingAction(null)}
      />

      <ConfirmDialog
        open={isConflictOpen}
        title="The campaign changed elsewhere"
        message="Somebody else changed this campaign, or its status moved on. Reload the data to see the current state."
        confirmLabel="Reload data"
        cancelLabel="Close"
        variant="warning"
        onConfirm={() => { setIsConflictOpen(false); reload(); }}
        onCancel={() => setIsConflictOpen(false)}
      />
    </div>
  );
};
